import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { AlertCircle, Plus, Loader2, Pencil, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { useLocalization } from '@/hooks/useLocalization';

type Severity = 'low' | 'medium' | 'high' | 'critical';
type Status   = 'open' | 'in_progress' | 'resolved' | 'closed';

interface Issue {
  id: string;
  title: string;
  description: string | null;
  severity: Severity;
  status: Status;
  owner_id: string | null;
  due_date: string | null;
  created_at: string;
}

const SEVERITY_CONFIG: Record<Severity, { label: string; className: string }> = {
  critical: { label: 'Critical', className: 'bg-destructive text-destructive-foreground' },
  high:     { label: 'High',     className: 'bg-amber-500 text-white' },
  medium:   { label: 'Medium',   className: 'bg-amber-400/80 text-amber-900' },
  low:      { label: 'Low',      className: 'bg-muted text-muted-foreground' },
};

const STATUS_CONFIG: Record<Status, { label: string; icon: React.ReactNode }> = {
  open:        { label: 'Open',        icon: <AlertCircle className="h-3 w-3" /> },
  in_progress: { label: 'In Progress', icon: <Clock className="h-3 w-3" /> },
  resolved:    { label: 'Resolved',    icon: <CheckCircle2 className="h-3 w-3" /> },
  closed:      { label: 'Closed',      icon: <CheckCircle2 className="h-3 w-3" /> },
};

const emptyForm = { title: '', description: '', severity: 'medium' as Severity, status: 'open' as Status, due_date: '' };

export const IssueRegister = () => {
  const { tx, t } = useLocalization();
  const { tenantId } = useAuth();
  const { toast } = useToast();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Issue | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');

  const load = useCallback(async () => {
    setLoading(true);
    const query = supabase
      .from('issues')
      .select('*')
      .order('created_at', { ascending: false });
    if (tenantId) query.eq('tenant_id', tenantId);
    const { data, error } = await query;
    if (error) { console.error(error); }
    else { setIssues((data ?? []) as Issue[]); }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (issue: Issue) => {
    setEditing(issue);
    setForm({ title: issue.title, description: issue.description ?? '', severity: issue.severity, status: issue.status, due_date: issue.due_date ?? '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast({ title: t('Title required'), variant: 'destructive' }); return; }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      description: form.description || null,
      severity: form.severity,
      status: form.status,
      due_date: form.due_date || null,
      tenant_id: tenantId,
    };

    let error;
    if (editing) {
      ({ error } = await supabase.from('issues').update(payload).eq('id', editing.id));
    } else {
      ({ error } = await supabase.from('issues').insert(payload));
    }
    setSaving(false);

    if (error) {
      toast({ title: t('Save failed'), description: error.message, variant: 'destructive' });
    } else {
      toast({ title: editing ? t('Issue updated') : t('Issue created') });
      setDialogOpen(false);
      load();
    }
  };

  const filtered = statusFilter === 'all' ? issues : issues.filter(i => i.status === statusFilter);
  const counts = { open: 0, in_progress: 0, resolved: 0, closed: 0 };
  issues.forEach(i => { counts[i.status]++; });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold text-foreground">{t('Issue Register')}</h2>
          </div>
          <p className="text-muted-foreground mt-1">{t('Track and resolve project issues, blockers, and escalations')}</p>
        </div>
        <Button onClick={openNew} className="gap-2">
          <Plus className="h-4 w-4" />  {t('New Issue')}
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {(['open', 'in_progress', 'resolved', 'closed'] as Status[]).map((s) => (
          <Card
            key={s}
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === s ? 'border-primary ring-1 ring-primary' : ''}`}
            onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground capitalize">{tx(STATUS_CONFIG[s].label)}</p>
                <p className="text-2xl font-bold mt-1">{counts[s]}</p>
              </div>
              <div className="text-muted-foreground">{STATUS_CONFIG[s].icon}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base">
              {statusFilter === 'all' ? t('All Issues') : t('{0} Issues', { 0: STATUS_CONFIG[statusFilter].label })}
            </CardTitle>
            <CardDescription>{filtered.length}  {t('issue')}{filtered.length !== 1 ? 's' : ''}</CardDescription>
          </div>
          {statusFilter !== 'all' && (
            <Button variant="ghost" size="sm" onClick={() => setStatusFilter('all')}>{t('Clear filter')}</Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <AlertTriangle className="h-8 w-8 mb-3 opacity-40" />
              <p className="text-sm">{t('No issues found')}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Issue')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Severity')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Status')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Due')}</th>
                  <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">{t('Created')}</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((issue) => (
                  <tr key={issue.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-3">
                      <p className="font-medium text-foreground">{tx(issue.title)}</p>
                      {issue.description && (
                        <p className="text-xs text-muted-foreground truncate max-w-xs mt-0.5">{tx(issue.description)}</p>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_CONFIG[issue.severity].className}`}>
                        {tx(SEVERITY_CONFIG[issue.severity].label)}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant="outline" className="gap-1 text-xs">
                        {STATUS_CONFIG[issue.status].icon}
                        {tx(STATUS_CONFIG[issue.status].label)}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {issue.due_date ? format(new Date(issue.due_date), 'MMM d, yyyy') : '—'}
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {format(new Date(issue.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-3">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(issue)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? t('Edit Issue') : t('New Issue')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t('Title *')}</Label>
              <Input placeholder={t('Brief description of the issue')} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>{t('Description')}</Label>
              <Textarea placeholder={t('Additional context, steps to reproduce, impact…')} rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('Severity')}</Label>
                <Select value={form.severity} onValueChange={v => setForm(f => ({ ...f, severity: v as Severity }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t('Low')}</SelectItem>
                    <SelectItem value="medium">{t('Medium')}</SelectItem>
                    <SelectItem value="high">{t('High')}</SelectItem>
                    <SelectItem value="critical">{t('Critical')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('Status')}</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v as Status }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">{t('Open')}</SelectItem>
                    <SelectItem value="in_progress">{t('In Progress')}</SelectItem>
                    <SelectItem value="resolved">{t('Resolved')}</SelectItem>
                    <SelectItem value="closed">{t('Closed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('Due Date')}</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}
              {editing ? t('Save Changes') : t('Create Issue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IssueRegister;
